import React from "react";
import { motion } from "motion/react";

// Synthesize authentic mechanical click sound using Web Audio API
export const playSwitchSound = (isLight: boolean) => {
  try {
    const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();
    if (ctx.state === "suspended") {
      ctx.resume();
    }
    const now = ctx.currentTime;

    // High snap click
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = "triangle";
    osc1.frequency.setValueAtTime(isLight ? 1600 : 1200, now);
    osc1.frequency.exponentialRampToValueAtTime(320, now + 0.022);
    gain1.gain.setValueAtTime(0.09, now);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.025);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(now);
    osc1.stop(now + 0.03);

    // Low mechanical thud
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = "sine";
    osc2.frequency.setValueAtTime(isLight ? 210 : 180, now);
    osc2.frequency.exponentialRampToValueAtTime(45, now + 0.04);
    gain2.gain.setValueAtTime(0.12, now);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.045);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(now);
    osc2.stop(now + 0.05);

    setTimeout(() => {
      ctx.close().catch(() => {});
    }, 100);
  } catch {
    // Graceful fallback if audio is blocked
  }
};

interface VintageToggleSwitchProps {
  theme: "dark" | "light";
  onToggle: () => void;
  className?: string;
  showSound?: boolean;
}

export const VintageToggleSwitch: React.FC<VintageToggleSwitchProps> = ({
  theme,
  onToggle,
  className = "",
  showSound = true,
}) => {
  const isLight = theme === "light";

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (showSound) {
      playSwitchSound(!isLight);
    }
    onToggle();
  };

  return (
    <button
      id="nav-vintage-toggle-btn"
      onClick={handleClick}
      type="button"
      className={`group relative flex items-center justify-center p-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E5983A] rounded-full select-none cursor-pointer transition-transform duration-150 active:scale-[0.97] ${className}`}
      aria-label={`Vintage toggle switch: currently in ${isLight ? "light" : "dark"} mode. Click to toggle to ${isLight ? "dark" : "light"} mode.`}
      title={isLight ? "Flip switch to Dark Charcoal Mode" : "Flip switch to Warm Ivory Day Mode"}
    >
      <svg
        viewBox="0 0 68 28"
        className="w-[62px] h-[25px] sm:w-[66px] sm:h-[27px] overflow-visible drop-shadow-md"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="vintagePlateDark" x1="0" y1="0" x2="68" y2="28" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#32302C" />
            <stop offset="35%" stopColor="#22211E" />
            <stop offset="70%" stopColor="#1C1B19" />
            <stop offset="100%" stopColor="#151413" />
          </linearGradient>
          <linearGradient id="vintagePlateLight" x1="0" y1="0" x2="68" y2="28" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#EDE9DE" />
            <stop offset="30%" stopColor="#DDD9CD" />
            <stop offset="70%" stopColor="#D2CEBF" />
            <stop offset="100%" stopColor="#C4BFAF" />
          </linearGradient>
          <linearGradient id="rimHighlightDark" x1="0" y1="0" x2="0" y2="28" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#57544C" />
            <stop offset="50%" stopColor="#2E2C28" />
            <stop offset="100%" stopColor="#0E0D0C" />
          </linearGradient>
          <linearGradient id="rimHighlightLight" x1="0" y1="0" x2="0" y2="28" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#FFFFFF" />
            <stop offset="50%" stopColor="#DDD9CE" />
            <stop offset="100%" stopColor="#9C9789" />
          </linearGradient>
          <linearGradient id="cavityShadow" x1="0" y1="0" x2="0" y2="14" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#080807" stopOpacity="0.95" />
            <stop offset="60%" stopColor="#131211" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#21201D" stopOpacity="0.6" />
          </linearGradient>
          <linearGradient id="leverBatonDark" x1="0" y1="0" x2="14" y2="14" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#F5F4F0" />
            <stop offset="25%" stopColor="#D5D2C8" />
            <stop offset="55%" stopColor="#8C887E" />
            <stop offset="85%" stopColor="#57544C" />
            <stop offset="100%" stopColor="#3A3833" />
          </linearGradient>
          <linearGradient id="leverBatonLight" x1="0" y1="0" x2="14" y2="14" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#FFF9E6" />
            <stop offset="30%" stopColor="#F5D77F" />
            <stop offset="70%" stopColor="#D4A738" />
            <stop offset="100%" stopColor="#99701E" />
          </linearGradient>
          <radialGradient id="pilotBulbOn" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#FFFFFF" stopOpacity="1" />
            <stop offset="25%" stopColor="#FEF3C7" stopOpacity="1" />
            <stop offset="65%" stopColor="#F59E0B" stopOpacity="0.95" />
            <stop offset="90%" stopColor="#D97706" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#92400E" stopOpacity="0.8" />
          </radialGradient>
          <radialGradient id="pilotBulbOff" cx="40%" cy="40%" r="50%">
            <stop offset="0%" stopColor="#78350F" stopOpacity="0.6" />
            <stop offset="60%" stopColor="#451A03" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#1A0B02" stopOpacity="0.9" />
          </radialGradient>
          <filter id="pilotGlow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>

        {/* Base Plate with Beveled Rim */}
        <rect
          x="1"
          y="1"
          width="66"
          height="26"
          rx="13"
          fill={isLight ? "url(#vintagePlateLight)" : "url(#vintagePlateDark)"}
          stroke={isLight ? "url(#rimHighlightLight)" : "url(#rimHighlightDark)"}
          strokeWidth="1.2"
        />
        <rect
          x="2.6"
          y="2.6"
          width="62.8"
          height="22.8"
          rx="11.4"
          fill="none"
          stroke={isLight ? "#BFBAAB" : "#2A2825"}
          strokeWidth="0.6"
          strokeDasharray="100"
          opacity="0.8"
        />

        {/* Left Screw */}
        <g transform="translate(7.5, 14)">
          <circle cx="0" cy="0" r="1.9" fill={isLight ? "#BDB8A8" : "#2C2A26"} stroke={isLight ? "#8A8576" : "#141413"} strokeWidth="0.4" />
          <line x1="-1.3" y1="-0.7" x2="1.3" y2="0.7" stroke={isLight ? "#666255" : "#0D0D0C"} strokeWidth="0.5" strokeLinecap="round" />
        </g>

        {/* Right Screw */}
        <g transform="translate(60.5, 14)">
          <circle cx="0" cy="0" r="1.9" fill={isLight ? "#BDB8A8" : "#2C2A26"} stroke={isLight ? "#8A8576" : "#141413"} strokeWidth="0.4" />
          <line x1="-1.2" y1="0.9" x2="1.2" y2="-0.9" stroke={isLight ? "#666255" : "#0D0D0C"} strokeWidth="0.5" strokeLinecap="round" />
        </g>

        {/* Switch Cavity Slot */}
        <g transform="translate(13.5, 7)">
          <rect
            x="0"
            y="0"
            width="41"
            height="14"
            rx="7"
            fill={isLight ? "#CDC8BB" : "#151413"}
            stroke={isLight ? "#AFA99A" : "#33312C"}
            strokeWidth="0.8"
          />
          <rect x="1" y="1" width="39" height="12" rx="6" fill="url(#cavityShadow)" />
        </g>

        {/* Indicator Pilot Lamp */}
        <g transform="translate(34, 4)">
          <circle
            cx="0"
            cy="0"
            r="2.2"
            fill={isLight ? "#B45309" : "#3D3B36"}
            stroke={isLight ? "#D97706" : "#262522"}
            strokeWidth="0.4"
          />
          <circle
            cx="0"
            cy="0"
            r="1.6"
            fill={isLight ? "url(#pilotBulbOn)" : "url(#pilotBulbOff)"}
            filter={isLight ? "url(#pilotGlow)" : undefined}
            className="transition-all duration-300"
          />
          <circle cx="-0.4" cy="-0.4" r="0.5" fill="#FFFFFF" opacity={isLight ? 0.9 : 0.3} />
        </g>

        {/* Mechanical Lever with Framer-Motion Spring Physics */}
        <motion.g
          animate={{ x: isLight ? 43 : 25 }}
          transition={{ type: "spring", stiffness: 420, damping: 24, mass: 0.8 }}
        >
          {/* Lever Shadow */}
          <motion.ellipse
            cx={isLight ? -2.5 : 2.5}
            cy="15.5"
            rx="4.8"
            ry="3"
            fill="#000000"
            opacity={0.45}
            animate={{ cx: isLight ? -3 : 3, scaleX: isLight ? 1.15 : 0.95 }}
            transition={{ duration: 0.2 }}
          />

          {/* Lever Collar Ring */}
          <circle cx="0" cy="14" r="5.5" fill={isLight ? "#99701E" : "#2D2B27"} stroke={isLight ? "#F5D77F" : "#4D4A43"} strokeWidth="0.7" />
          <circle cx="0" cy="14" r="4" fill={isLight ? "#593F0C" : "#1A1917"} />

          {/* Baton Shaft */}
          <motion.path
            d="M -1.8 14 L -1.1 6.8 L 1.1 6.8 L 1.8 14 Z"
            fill={isLight ? "url(#leverBatonLight)" : "url(#leverBatonDark)"}
            stroke={isLight ? "#99701E" : "#33312C"}
            strokeWidth="0.35"
            animate={{
              transform: isLight ? "rotate(18deg)" : "rotate(-18deg)",
              transformOrigin: "0px 14px",
            }}
            transition={{ type: "spring", stiffness: 450, damping: 22 }}
          />

          {/* Baton Spherical Knob */}
          <motion.g
            animate={{
              transform: isLight ? "rotate(18deg)" : "rotate(-18deg)",
              transformOrigin: "0px 14px",
            }}
            transition={{ type: "spring", stiffness: 450, damping: 22 }}
          >
            <circle
              cx="0"
              cy="5.5"
              r="3.5"
              fill={isLight ? "url(#leverBatonLight)" : "url(#leverBatonDark)"}
              stroke={isLight ? "#FFF" : "#A3A199"}
              strokeWidth="0.45"
            />
            <ellipse
              cx="-1"
              cy="4.5"
              rx="1.1"
              ry="1.6"
              transform="rotate(-25 -1 4.5)"
              fill="#FFFFFF"
              opacity={isLight ? 0.9 : 0.75}
            />
            <line x1="-2" y1="5.5" x2="2" y2="5.5" stroke={isLight ? "#8A6216" : "#2E2D2A"} strokeWidth="0.35" opacity="0.7" />
            <line x1="-1.6" y1="6.5" x2="1.6" y2="6.5" stroke={isLight ? "#8A6216" : "#2E2D2A"} strokeWidth="0.35" opacity="0.7" />
          </motion.g>
        </motion.g>
      </svg>
    </button>
  );
};
